import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  defaultHeaders,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getApiBaseUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  ReceiptBatchControllerBodyTypes,
  ReceiptBatchControllerQueryTypes,
  ReceiptBatchControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import {
  ReceiptBatchControllerDefinition,
  SubmitReceiptBatchError,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { generatePath } from '@pflegenavi/frontend/routing';

interface SubmitReceiptBatchServiceProviderInfoIncompleteException
  extends Error {
  errorCode: SubmitReceiptBatchError.SERVICE_PROVIDER_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchServiceProviderInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchServiceProviderInfoIncompleteException => {
  return (
    ('errorCode' in error &&
      error.errorCode ===
        SubmitReceiptBatchError.SERVICE_PROVIDER_INFO_INCOMPLETE) ||
    // TODO: We should determine how to best structure error handling..
    // It should be possible to provide translations in the backend using gettext,
    // then we can display those directly.
    // The language could be set using a header.
    ('errors' in error &&
      'detail' in error.errors &&
      error.errors.detail?.startsWith('Invalid service provider data'))
  );
};

interface SubmitReceiptBatchServiceProviderUserIncompleteException
  extends Error {
  errorCode: SubmitReceiptBatchError.SERVICE_PROVIDER_USER_INCOMPLETE;
}

export const isSubmitReceiptBatchServiceProviderUserIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchServiceProviderUserIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.SERVICE_PROVIDER_USER_INCOMPLETE
  );
};

interface SubmitReceiptBatchNursingHomeInfoIncompleteException extends Error {
  errorCode: SubmitReceiptBatchError.NURSING_HOME_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchNursingHomeInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchNursingHomeInfoIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.NURSING_HOME_INFO_INCOMPLETE
  );
};

interface SubmitReceiptBatchResidentInfoIncompleteException extends Error {
  errorCode: SubmitReceiptBatchError.RESIDENT_INFO_INCOMPLETE;
}

export const isSubmitReceiptBatchResidentInfoIncompleteException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): error is SubmitReceiptBatchResidentInfoIncompleteException => {
  return (
    'errorCode' in error &&
    error.errorCode === SubmitReceiptBatchError.RESIDENT_INFO_INCOMPLETE
  );
};

export const isSubmitReceiptBatchDateError = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): boolean => {
  return (
    'statusCode' in error &&
    error.statusCode === 422 &&
    (error.message.includes(
      'If useIndividualReceiptDates is false, receiptDate must be set'
    ) ||
      error.message.includes(
        'Failed to generate receipts. Receipt date not found.'
      ) ||
      error.message.includes(
        'Failed to generate receipts. No receipt date found.'
      ))
  );
};

export interface IReceiptBatchApi extends Api {
  getReceiptBatchList(dataIn: {
    params: ReceiptBatchControllerQueryTypes['getReceiptBatchList'];
  }): Promise<ReceiptBatchControllerResultTypes['getReceiptBatchList']>;

  getReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchControllerResultTypes['getReceiptBatch']>;

  postReceiptBatch(dataIn: {
    body: ReceiptBatchControllerBodyTypes['postReceiptBatch'];
  }): Promise<ReceiptBatchControllerResultTypes['postReceiptBatch']>;

  putReceiptBatch(dataIn: {
    params: { batchId: string };
    body: ReceiptBatchControllerBodyTypes['putReceiptBatch'];
  }): Promise<ReceiptBatchControllerResultTypes['putReceiptBatch']>;

  patchReceiptBatch(dataIn: {
    params: { batchId: string };
    body: ReceiptBatchControllerBodyTypes['patchReceiptBatch'];
  }): Promise<ReceiptBatchControllerResultTypes['patchReceiptBatch']>;

  postReceiptBatchEntry(dataIn: {
    params: { batchId: string };
    body: ReceiptBatchControllerBodyTypes['postReceiptBatchEntry'];
  }): Promise<ReceiptBatchControllerResultTypes['postReceiptBatchEntry']>;

  patchReceiptBatchEntry(dataIn: {
    params: { batchId: string; entryId: string };
    body: ReceiptBatchControllerBodyTypes['patchReceiptBatchEntry'];
  }): Promise<ReceiptBatchControllerResultTypes['patchReceiptBatchEntry']>;

  deleteReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchControllerResultTypes['deleteReceiptBatch']>;

  deleteReceiptBatchEntry(dataIn: {
    params: {
      batchId: string;
      entryId: string;
    };
  }): Promise<ReceiptBatchControllerResultTypes['deleteReceiptBatchEntry']>;

  submitReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchControllerResultTypes['submitReceiptBatch']>;

  retractReceiptBatch(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchControllerResultTypes['retractReceiptBatch']>;

  generateReceiptBatchEntryPdf(dataIn: {
    params: { batchId: string; entryId: string };
  }): Promise<ReceiptBatchControllerResultTypes['generateReceiptPdf']>;

  generateReceiptBatchPdf(dataIn: {
    params: { batchId: string };
  }): Promise<ReceiptBatchControllerResultTypes['generateReceiptBatchPdf']>;
}

export class ReceiptBatchApi implements IReceiptBatchApi {
  baseUrl: string;

  getReceiptBatchList: IReceiptBatchApi['getReceiptBatchList'];
  getReceiptBatch: IReceiptBatchApi['getReceiptBatch'];
  postReceiptBatch: IReceiptBatchApi['postReceiptBatch'];
  putReceiptBatch: IReceiptBatchApi['putReceiptBatch'];
  patchReceiptBatch: IReceiptBatchApi['patchReceiptBatch'];
  postReceiptBatchEntry: IReceiptBatchApi['postReceiptBatchEntry'];
  patchReceiptBatchEntry: IReceiptBatchApi['patchReceiptBatchEntry'];
  deleteReceiptBatch: IReceiptBatchApi['deleteReceiptBatch'];
  deleteReceiptBatchEntry: IReceiptBatchApi['deleteReceiptBatchEntry'];
  submitReceiptBatch: IReceiptBatchApi['submitReceiptBatch'];
  retractReceiptBatch: IReceiptBatchApi['retractReceiptBatch'];
  generateReceiptBatchEntryPdf: IReceiptBatchApi['generateReceiptBatchEntryPdf'];
  generateReceiptBatchPdf: IReceiptBatchApi['generateReceiptBatchPdf'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getApiBaseUrl(tenantId, apiUrl);

    this.getReceiptBatchList = get({
      authContext,

      url: (opts) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', opts.page.toString());
        queryParams.append('pageSize', opts.pageSize.toString());

        if (opts.state) {
          for (const state of opts.state) {
            queryParams.append('state[]', state);
          }
        }

        const fields = ['lt', 'gt', 'eq', 'gte', 'lte'] as const;
        fields.forEach((field) => {
          const amountField = opts.amount?.[field];
          if (amountField) {
            queryParams.append(`amount[${field}]`, amountField.toString());
          }

          const receiptCountField = opts.receiptCount?.[field];
          if (receiptCountField) {
            queryParams.append(
              `receiptCount[${field}]`,
              receiptCountField.toString()
            );
          }

          const receiptDateField = opts.receiptDateRange?.[field];
          if (receiptDateField) {
            queryParams.append(
              `receiptDateRange[${field}]`,
              receiptDateField.toISOString()
            );
          }

          const acceptedDateField = opts.acceptedDateRange?.[field];
          if (acceptedDateField) {
            queryParams.append(
              `acceptedDateRange[${field}]`,
              acceptedDateField.toISOString()
            );
          }
        });

        if (opts?.sort) {
          queryParams.append('sort', opts.sort);
        }

        if (opts?.sortOrder) {
          queryParams.append('sortOrder', opts.sortOrder);
        }

        const queryString = queryParams.toString();

        const paginated =
          ReceiptBatchControllerDefinition.endpoints.getReceiptBatchList.url;

        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${paginated}?${queryString}`;
      },
    });

    this.getReceiptBatch = get({
      authContext,
      url: (opts) => {
        const getReceiptBatch = generatePath(
          ReceiptBatchControllerDefinition.endpoints.getReceiptBatch.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${getReceiptBatch}`;
      },
    });

    this.postReceiptBatch = modify({
      authContext,
      method: 'POST',
      url: () => {
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${ReceiptBatchControllerDefinition.endpoints.postReceiptBatch.url}`;
      },
    });

    this.putReceiptBatch = modify({
      authContext,
      method: 'PUT',
      url: (opts) => {
        const putReceiptBatch = generatePath(
          ReceiptBatchControllerDefinition.endpoints.putReceiptBatch.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${putReceiptBatch}`;
      },
    });

    this.patchReceiptBatch = modify({
      authContext,
      method: 'PATCH',
      url: (opts) => {
        const patchReceiptBatch = generatePath(
          ReceiptBatchControllerDefinition.endpoints.patchReceiptBatch.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${patchReceiptBatch}`;
      },
    });

    this.postReceiptBatchEntry = modify({
      authContext,
      method: 'POST',
      url: (opts) => {
        const postReceiptBatchEntry = generatePath(
          ReceiptBatchControllerDefinition.endpoints.postReceiptBatchEntry.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${postReceiptBatchEntry}`;
      },
    });

    this.patchReceiptBatchEntry = modify({
      authContext,
      method: 'PATCH',
      url: (opts) => {
        const patchReceiptBatchEntry = generatePath(
          ReceiptBatchControllerDefinition.endpoints.patchReceiptBatchEntry.url,
          {
            batchId: opts.batchId,
            entryId: opts.entryId,
          }
        );

        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${patchReceiptBatchEntry}`;
      },
    });

    this.deleteReceiptBatch = modify({
      authContext,
      method: 'DELETE',
      url: (opts) => {
        const deleteReceiptBatch = generatePath(
          ReceiptBatchControllerDefinition.endpoints.deleteReceiptBatch.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${deleteReceiptBatch}`;
      },
    });

    this.deleteReceiptBatchEntry = modify({
      authContext,
      method: 'DELETE',
      url: (opts) => {
        const deleteReceiptBatchEntry = generatePath(
          ReceiptBatchControllerDefinition.endpoints.deleteReceiptBatchEntry
            .url,
          {
            batchId: opts.batchId,
            entryId: opts.entryId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${deleteReceiptBatchEntry}`;
      },
    });

    this.submitReceiptBatch = modify({
      authContext,
      method: 'POST',
      url: (opts) => {
        const submitReceiptBatch = generatePath(
          ReceiptBatchControllerDefinition.endpoints.submitReceiptBatch.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${submitReceiptBatch}`;
      },
    });

    this.retractReceiptBatch = modify({
      authContext,
      method: 'POST',
      url: (opts) => {
        const retractReceiptBatch = generatePath(
          ReceiptBatchControllerDefinition.endpoints.retractReceiptBatch.url,
          {
            batchId: opts.batchId,
          }
        );
        return `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${retractReceiptBatch}`;
      },
    });

    this.generateReceiptBatchEntryPdf = async (dataIn) => {
      const generateReceiptPdf = generatePath(
        ReceiptBatchControllerDefinition.endpoints.generateReceiptPdf.url,
        {
          batchId: dataIn.params.batchId,
          entryId: dataIn.params.entryId,
        }
      );
      const finalUrl = `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${generateReceiptPdf}`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };

    this.generateReceiptBatchPdf = async (dataIn) => {
      const generateReceiptBatchPdf = generatePath(
        ReceiptBatchControllerDefinition.endpoints.generateReceiptBatchPdf.url,
        {
          batchId: dataIn.params.batchId,
        }
      );
      const finalUrl = `${this.baseUrl}/${ReceiptBatchControllerDefinition.controller}/${generateReceiptBatchPdf}`;

      return await superFetch(authContext, finalUrl, {
        method: 'POST',
        headers: defaultHeaders(),
      }).then((res) => res.blob());
    };
  }
}
